import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import "../../../css/createSourceModal.css";
import SwalAlertComp from "../../SwalAlert";
import LoginButton from "../../molecules/LoginButton";
import { ClipLoader } from "react-spinners";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import {
    getGoogleSheetsLeadAuthUrl,
    googleSheetsLeadSpreadsheets,
    googleSheetsLeadPages,
    googleSheetsLeadFields,
} from "../../../helpers/leads/googleSheets/googleSheetsLead";
import { Box } from "@mui/material";

const GoogleSheetsLeadDestination = ({
    setGoogleSheetsData,
    code,
    isButtonLoader,
    setIsButtonLoader,
}) => {
    const language = useSelector((state) => state.languagesState.languages);

    const [name, setName] = useState("");
    const [authUrl, setAuthUrl] = useState("");
    const [progressStep, setProgressStep] = useState(0);

    const [installId, setInstallId] = useState(null);
    const [spreadsheets, setSpreadsheets] = useState([]);
    const [selectedSpreadsheet, setSelectedSpreadsheet] = useState(null);
    const [pages, setPages] = useState([]);
    const [selectedPage, setSelectedPage] = useState(null);
    const [fields, setFields] = useState([]);

    const [isLoading, setIsLoading] = useState(false);


    useEffect(() => {
        const fetchAuthUrl = async () => {
            setIsLoading(true);
            const response = await getGoogleSheetsLeadAuthUrl();
            if (response?.status) {
                setAuthUrl(response?.data?.url ?? "");
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response?.message ?? "Failed to fetch auth URL",
                    icon: "error",
                });
            }
            setIsLoading(false);
        };
        fetchAuthUrl();
    }, []);

    const isRun = useRef(false);

    const handleAuthSuccess = async (data) => {
        if (isRun.current) return;
        if (data.code) isRun.current = true;
        setIsLoading(true);
        if (setIsButtonLoader) setIsButtonLoader(true);

        const response = await googleSheetsLeadSpreadsheets({
            code: data.code,
        });

        if (response?.status) {
            setInstallId(response?.data?.install_id);
            setSpreadsheets(response?.data?.spreadsheets || []);
            setProgressStep(1);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response?.message ?? "Authorization failed",
                icon: "error",
            });
        }

        setIsLoading(false);
        if (setIsButtonLoader) setIsButtonLoader(false);
    };

    const handleNameChange = (e) => {
        setName(e.target.value);
    };

    const handleSpreadsheetChange = async (event, selectedOpt) => {
        if (selectedOpt) {
            setSelectedSpreadsheet(selectedOpt);
            setSelectedPage(null);
            setFields([]);

            setIsLoading(true);
            const response = await googleSheetsLeadPages({
                install_id: installId,
                spreadsheet_id: selectedOpt.id,
            });

            if (response?.status) {
                setPages(
                    response?.data?.pages?.map((page) => page?.name) || [],
                );
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response?.message ?? "Failed to fetch pages",
                    icon: "error",
                });
            }
            setIsLoading(false);
        } else {
            setSelectedSpreadsheet(null);
            setPages([]);
            setSelectedPage(null);
            setFields([]);
        }
    };

    const handlePageChange = async (event, pageName) => {
        if (pageName) {
            setSelectedPage(pageName);

            setIsLoading(true);
            const response = await googleSheetsLeadFields({
                install_id: installId,
                spreadsheet_id: selectedSpreadsheet?.id,
                page_name: pageName,
            });

            if (response?.status) {
                setFields(response?.data?.fields || []);
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response?.message ?? "Failed to fetch fields",
                    icon: "error",
                });
            }
            setIsLoading(false);
        } else {
            setSelectedPage(null);
            setFields([]);
        }
    };

    useEffect(() => {
        if (setGoogleSheetsData && name && installId) {
            setGoogleSheetsData({
                name,
                install_id: installId,
                spreadsheet_id: selectedSpreadsheet?.id,
                spreadsheet_name: selectedSpreadsheet?.name,
                page_name: selectedPage,
            });
        }
    }, [
        name,
        installId,
        selectedSpreadsheet,
        selectedPage,
        setGoogleSheetsData,
    ]);

    return (
        <Box style={{ marginTop: "10px" }}>
            {progressStep === 0 && (
                <div>
                    <FormControl className="mb-3" style={{ width: "100%" }}>
                        <TextField
                            name="name"
                            label={language?.source_modal?.name ?? "Name"}
                            variant="outlined"
                            onChange={handleNameChange}
                            placeholder={
                                language?.source_modal?.please_write_name ??
                                "Please Write Name"
                            }
                            value={name}
                            required
                        />
                    </FormControl>

                    <div className="position-relative overflow-hidden z-1">
                        {(isLoading || isButtonLoader) && (
                            <div className="circle-loader">
                                <ClipLoader color={"#000"} size={17} />
                            </div>
                        )}
                        <LoginButton
                            url={authUrl}
                            code="google-sheets"
                            paramNames={["code"]}
                            onSuccess={handleAuthSuccess}
                            logoSrc="/assets/images/typeImages/google-sheets-icon.png"
                            buttonText={
                                language?.buttons?.connect_with_google_sheets ??
                                "Connect with Google Sheets"
                            }
                            disabled={!name || isLoading}
                            variant="contained"
                            color="primary"
                            logoWidth={30}
                        />
                    </div>
                </div>
            )}

            {progressStep === 1 && (
                <div>
                    <div className="mb-3">
                        <FormControl fullWidth>
                            <Autocomplete
                                name="spreadsheet"
                                onChange={handleSpreadsheetChange}
                                value={selectedSpreadsheet}
                                options={spreadsheets}
                                getOptionLabel={(option) => option?.name ?? ""}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Spreadsheet"
                                        placeholder="Search for a spreadsheet..."
                                        variant="outlined"
                                    />
                                )}
                            />
                        </FormControl>
                    </div>

                    {selectedSpreadsheet && (
                        <div className="mb-3">
                            <FormControl fullWidth>
                                <Autocomplete
                                    name="page"
                                    onChange={handlePageChange}
                                    value={selectedPage || null}
                                    options={pages}
                                    getOptionLabel={(option) =>
                                        typeof option === "string"
                                            ? option
                                            : (option ?? "")
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Sheet (Page)"
                                            placeholder="Search for a sheet..."
                                            variant="outlined"
                                        />
                                    )}
                                />
                            </FormControl>
                        </div>
                    )}
                </div>
            )}
        </Box>
    );
};

export default GoogleSheetsLeadDestination;
